import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Gem, Wallet, Users, Inbox, CheckSquare, FolderOpen,
  FileText, Shield, ShieldCheck, Settings, LogOut, Menu, X, Briefcase,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DiamondMark from "@/components/DiamondMark";
import NotificationBell from "@/components/admin/NotificationBell";

const NAV = [
  { to: "/admin", label: "CEO Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/ventures", label: "Ventures", icon: Gem },
  { to: "/admin/business-services", label: "Business Services", icon: Briefcase },
  { to: "/admin/finance", label: "Finance", icon: Wallet },
  { to: "/admin/crm", label: "Sales / CRM", icon: Users },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/tasks", label: "Tasks & Projects", icon: CheckSquare },
  { to: "/admin/documents", label: "Documents", icon: FolderOpen },
  { to: "/admin/insights", label: "Website Content", icon: FileText },
  { to: "/admin/security", label: "Security", icon: ShieldCheck },
  { to: "/admin/users", label: "Users & Roles", icon: Shield, adminOnly: true },
  { to: "/admin/settings", label: "Settings", icon: Settings, adminOnly: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "ceo" || user?.role === "admin";

  const doLogout = async () => { await logout(); navigate("/login"); };

  const links = NAV.filter((n) => !n.adminOnly || isAdmin);

  const SidebarInner = (
    <>
      <Link to="/admin" className="flex items-center gap-3 px-6 h-20 border-b border-white/10">
        <DiamondMark size={28} />
        <div className="leading-none">
          <span className="block font-serif text-lg text-bd-ivory">Black Diamond</span>
          <span className="block overline text-bd-gold text-[0.55rem]">Group Control</span>
        </div>
      </Link>
      <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1">
        {links.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-200 ${
                isActive ? "bg-bd-gold/10 text-bd-gold border-l-2 border-bd-gold" : "text-bd-ivory/55 hover:text-bd-ivory hover:bg-white/5 border-l-2 border-transparent"
              }`
            }
          >
            <n.icon size={17} strokeWidth={1.5} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="flex items-center gap-3 mb-3 px-2">
          <div className="w-9 h-9 rounded-full bg-bd-gold/20 flex items-center justify-center text-bd-gold font-serif text-lg">
            {(user?.name || "U")[0]}
          </div>
          <div className="min-w-0">
            <p className="text-sm text-bd-ivory truncate">{user?.name}</p>
            <p className="text-[0.65rem] uppercase tracking-wider text-bd-gold">{user?.role}</p>
          </div>
        </div>
        <button data-testid="admin-logout" onClick={doLogout} className="w-full flex items-center gap-2 px-2 py-2 text-xs text-bd-ivory/50 hover:text-red-400 transition-colors">
          <LogOut size={15} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-bd-black flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-bd-charcoal border-r border-white/10 flex-col z-40">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="relative w-72 bg-bd-charcoal border-r border-white/10 flex flex-col">
            {SidebarInner}
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-bd-charcoal border-b border-white/10 h-16 flex items-center justify-between px-4">
          <button data-testid="admin-mobile-toggle" onClick={() => setOpen(true)} className="text-bd-ivory p-2"><Menu size={22} /></button>
          <span className="font-serif text-bd-ivory text-lg">Group Control</span>
          <NotificationBell />
        </header>
        <header className="hidden lg:flex sticky top-0 z-30 bg-bd-black/80 backdrop-blur-xl border-b border-white/5 h-16 items-center justify-end px-10 gap-2">
          <NotificationBell />
        </header>
        <main key={location.pathname} className="p-6 md:p-10 max-w-[1400px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
